/**
 * Adaptive render density (as Gravity's P10.13, measured there: drawing pixels, not game logic,
 * limits slow devices). The game starts sharp and, if frames stay slow for a few seconds, drops
 * to 1× density for the rest of the session. It never switches back, so the picture doesn't flip
 * between sharp and soft. Windows are measured in time, not frames, so a very slow device
 * decides as quickly as a fast one (within about 6 s).
 */

/** Time ignored at the start (asset upload, shader compilation). */
const WARMUP_MS = 2000;
/** Length of one measuring window. */
const WINDOW_MS = 2000;
/** A window averaging longer frames than this (under ~45 fps) is "slow". */
const SLOW_FRAME_MS = 1000 / 45;
/** Consecutive slow windows before lowering the density. */
const SLOW_WINDOWS = 2;
/**
 * Longer gaps are tab switches or loading hitches, not rendering cost (a hidden tab pauses the
 * frames for seconds). A struggling software renderer can take several hundred ms per frame.
 */
const MAX_COUNTED_FRAME_MS = 1000;

/** Decides, from frame times, when to give up on high density. Pure. */
export class ResolutionGovernor {
	private warm = 0;
	private frames = 0;
	private total = 0;
	private slowWindows = 0;
	private decided = false;

	/** Feeds one frame's duration; returns true exactly once, when it is time to lower. */
	frame(deltaMs: number): boolean {
		if (this.decided || deltaMs > MAX_COUNTED_FRAME_MS || deltaMs <= 0) return false;
		if (this.warm < WARMUP_MS) {
			this.warm += deltaMs;
			return false;
		}
		this.frames++;
		this.total += deltaMs;
		if (this.total < WINDOW_MS) return false;
		const slow = this.total / this.frames > SLOW_FRAME_MS;
		this.frames = 0;
		this.total = 0;
		this.slowWindows = slow ? this.slowWindows + 1 : 0;
		if (this.slowWindows < SLOW_WINDOWS) return false;
		this.decided = true;
		return true;
	}
}
